import { useInfiniteQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Bookmark } from 'lucide-react';
import type { Pagina } from '@malgoos/shared';
import { Botao, Selo } from '../../componentes/base';
import { Estado } from '../../componentes/estados';
import { linkDaVisao, type CompartilhamentoVisao } from '../../componentes/compartilhar-visao';
import { get } from '../../nucleo/api';
import { carregarMais } from '../../nucleo/foco';
import { nomePessoa } from '../../nucleo/formato';
import { caminhoDaAba } from '../../nucleo/modulos-web';
import { abaDaTela, nomeDaTela } from '../../nucleo/nome-da-tela';
import { useSessao } from '../../nucleo/sessao';

/**
 * E1 (lote 09/10, item 6, opção A, a parte das listas; achado R37-SPEC-M2): as visões salvas das listas marcadas "Mostrar
 * em Relatórios", na página de entrada, depois de "Meus relatórios" e "Compartilhados comigo". O cartão é o mesmo do
 * relatório salvo; abrir é ir à lista da tela com `?visao_salva=<id>` (a `Tabela` aplica os filtros da visão, e a lista roda
 * com as permissões e a empresa de quem abre). O servidor só devolve as das telas que a pessoa lê; paginada por cursor.
 */
export interface VisaoEmRelatorios {
  id: string;
  tela: string;
  nome: string;
  meu: boolean;
  autor: string | null;
  compartilhamento: CompartilhamentoVisao;
}

/** O endereço da lista da visão (null quando a tela saiu do código). */
export function caminhoDaVisao(v: Pick<VisaoEmRelatorios, 'id' | 'tela'>): string | null {
  const achada = abaDaTela(v.tela);
  const caminho = achada ? caminhoDaAba(achada.modulo.slug, achada.aba.chave) : null;
  // Review r38 spec (B3): a lista numa sub-aba (Progresso "Por cursos") leva a tela no link para a página abrir a sub-aba certa.
  return caminho ? linkDaVisao(caminho, v.id, achada?.sub ? v.tela : undefined) : null;
}

export function SecaoVisoes() {
  const { t } = useTranslation('relatorios');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const q = useInfiniteQuery({
    queryKey: ['/relatorios/visoes'],
    queryFn: ({ pageParam }) => get<Pagina<VisaoEmRelatorios>>('/relatorios/visoes', { limite: 50, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (p) => p.proximo_cursor ?? undefined,
    enabled: s.pode('relatorios.ver'),
  });
  if (!s.pode('relatorios.ver')) return null;
  return (
    <Estado consulta={q}>
      {(d) => {
        const itens = d.pages.flatMap((p) => p.itens);
        if (!itens.length) return null;
        return (
          <section className="mt-6 flex min-w-0 flex-col gap-3" data-secao-salvos="visoes">
            <h2 className="text-base font-semibold text-tinta-1">{t('visoes.secao')}</h2>
            <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {itens.map((v) => {
                const destino = caminhoDaVisao(v);
                const tela = nomeDaTela(v.tela, tn);
                return (
                  <li key={v.id} className="min-w-0" data-visao-relatorios={v.id}>
                    <div className="flex h-full min-w-0 items-start gap-4 rounded-xl border border-borda bg-sup-1 p-4">
                      <span className="shrink-0 rounded-lg bg-acento-suave p-3 text-acento-forte" aria-hidden>
                        <Bookmark className="size-5" />
                      </span>
                      <div className="min-w-0 flex-1">
                        {destino ? (
                          <Link to={destino} className="block text-base font-semibold break-words text-tinta-1 hover:underline">
                            {v.nome}
                          </Link>
                        ) : (
                          <span className="block text-base font-semibold break-words text-tinta-1">{v.nome}</span>
                        )}
                        <span className="mt-1 block text-sm break-words text-tinta-2">{tela}</span>
                        <span className="mt-2 flex flex-wrap gap-1">
                          <Selo>{t('visoes.selo')}</Selo>
                          {v.meu && v.compartilhamento !== 'privado' && <Selo cor="verde">{t(`salvos.compartilhamentos.${v.compartilhamento}`)}</Selo>}
                          {!v.meu && v.autor && <Selo>{t('salvos.de', { nome: nomePessoa(v.autor) })}</Selo>}
                        </span>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
            {q.hasNextPage && (
              <div className="flex justify-center">
                <Botao variante="fantasma" carregando={q.isFetchingNextPage} onClick={(e) => carregarMais(() => q.fetchNextPage(), e.currentTarget)}>
                  {tn('comum.carregarMais')}
                </Botao>
              </div>
            )}
          </section>
        );
      }}
    </Estado>
  );
}
